import { serializeError } from "./serialized-error.js";

export function controlRoomWebSocketUrl({ room, baseUrl = globalThis.location?.href || "" }) {
  const url = new URL(`/control/${encodeURIComponent(room)}`, baseUrl);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  return url.toString();
}

export function createDeviceRelay({
  url,
  id,
  onCommand,
  onStatus = () => {},
  onError = () => {},
  WebSocketImpl = globalThis.WebSocket,
  reconnectInitialMs = 500,
  reconnectMaxMs = 30000,
} = {}) {
  if (!url || !id || !WebSocketImpl) throw new Error("Device relay requires url, id, and WebSocket.");
  let socket = null;
  let disposed = false;
  let reconnectTimer = 0;
  let backoff = reconnectInitialMs;
  let stateTimer = 0;
  let latestState = null;

  const send = (message) => {
    if (socket?.readyState === WebSocketImpl.OPEN) socket.send(JSON.stringify(message));
  };

  const connect = () => {
    if (disposed) return;
    onStatus("connecting");
    socket = new WebSocketImpl(url);
    socket.addEventListener("open", () => {
      backoff = reconnectInitialMs;
      onStatus("open");
      send({ type: "hello", role: "renderer", id });
      if (latestState) send({ type: "state", ...latestState });
    });
    socket.addEventListener("message", async (event) => {
      let message;
      try { message = JSON.parse(event.data); } catch { return; }
      if (message?.type !== "command" || typeof message.requestId !== "string") return;
      try {
        const result = await onCommand(message.command, message.payload || {}, message);
        send({ type: "commandResult", requestId: message.requestId, ok: true, result });
      } catch (error) {
        send({
          type: "commandResult",
          requestId: message.requestId,
          ok: false,
          error: serializeError(error),
        });
      }
    });
    socket.addEventListener("error", onError);
    socket.addEventListener("close", (event) => {
      socket = null;
      onStatus("closed");
      if (disposed || [1003, 1008, 4001].includes(event.code)) return;
      reconnectTimer = window.setTimeout(connect, backoff);
      backoff = Math.min(backoff * 2, reconnectMaxMs);
    });
  };

  const publishState = (state) => {
    latestState = state;
    if (stateTimer) return;
    stateTimer = window.setTimeout(() => {
      stateTimer = 0;
      send({ type: "state", ...latestState });
    }, 500);
  };

  const destroy = () => {
    disposed = true;
    if (reconnectTimer) window.clearTimeout(reconnectTimer);
    if (stateTimer) window.clearTimeout(stateTimer);
    socket?.close(1000, "app closed");
    socket = null;
  };

  connect();
  return { destroy, publishState };
}
